import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

/**
 * MAKAI shared page primitives.
 *
 * Use these on every dashboard / module page for consistent spacing,
 * typography and motion across the platform.
 *
 *   <PageHeader title="Gradebook" subtitle="Term 2 · S3 Maths" actions={…} />
 *   <PageSection title="Today">…</PageSection>
 *   <StatCard label="Outstanding" value="UGX 4.2M" trend={-3} />
 *   <EmptyState title="No learners yet" cta={{ label: "Add learner", to: "…"}} />
 *   <AICallout suggestions={[…]} onAsk={…} />
 */

export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={cn(
        "grid grid-cols-[minmax(0,1fr)_auto] items-end gap-4 pb-5 sm:flex sm:flex-wrap sm:justify-between sm:pb-6",
        className,
      )}
    >
      <div className="min-w-0">
        {eyebrow && (
          <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-primary">
            {eyebrow}
          </div>
        )}
        <h1 className="truncate font-display text-2xl font-semibold leading-tight tracking-tight sm:text-3xl">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground sm:text-[15px]">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function PageSection({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("animate-fade-in space-y-3", className)}>
      {(title || actions) && (
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            {title && (
              <h2 className="font-display text-base font-semibold tracking-tight sm:text-lg">{title}</h2>
            )}
            {description && (
              <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">{description}</p>
            )}
          </div>
          {actions && <div className="shrink-0">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function StatCard({
  label,
  value,
  hint,
  trend,
  icon,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  trend?: number | null;
  icon?: ReactNode;
  className?: string;
}) {
  const trendColor =
    trend == null
      ? "text-muted-foreground"
      : trend > 0
        ? "text-emerald-600 dark:text-emerald-400"
        : trend < 0
          ? "text-destructive"
          : "text-muted-foreground";
  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-2xl border bg-card p-4 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-card",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </div>
          <div className="mt-1.5 font-display text-2xl font-semibold leading-none tracking-tight">
            {value}
          </div>
          {hint && <div className="mt-1.5 text-xs text-muted-foreground">{hint}</div>}
        </div>
        {icon && (
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
            {icon}
          </span>
        )}
      </div>
      {trend != null && (
        <div className={cn("mt-2 inline-flex items-center gap-1 text-xs font-medium", trendColor)}>
          {trend > 0 ? "▲" : trend < 0 ? "▼" : "•"} {Math.abs(trend).toFixed(1)}%
        </div>
      )}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  cta,
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  cta?: { label: string; to?: string; onClick?: () => void };
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center rounded-2xl border border-dashed bg-card/50 px-6 py-10 text-center",
        className,
      )}
    >
      <div className="grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
        {icon ?? <Sparkles className="h-5 w-5" />}
      </div>
      <h3 className="mt-3 font-display text-base font-semibold">{title}</h3>
      {description && (
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>
      )}
      {cta && (
        <div className="mt-4">
          {cta.to ? (
            <Button asChild>
              <Link to={cta.to as never}>
                {cta.label} <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          ) : (
            <Button onClick={cta.onClick}>
              {cta.label} <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export function LoadingSkeleton({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn("animate-pulse space-y-2", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-12 rounded-xl bg-muted/60" />
      ))}
    </div>
  );
}

/**
 * Inline contextual "Ask MAKAI AI" card. Drop into any module page to give the
 * AI a permanent home inside the workflow. Clicking a suggestion opens the
 * global Copilot panel pre-filled by dispatching a window event the FAB
 * listens to.
 */
export function AICallout({
  title = "Ask MAKAI AI",
  description,
  suggestions,
  className,
}: {
  title?: string;
  description?: string;
  suggestions: string[];
  className?: string;
}) {
  function ask(prompt: string) {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("makai:ask", { detail: { prompt } }));
  }
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border bg-gradient-to-br from-primary/8 via-card to-card p-4 shadow-soft sm:p-5",
        className,
      )}
    >
      <div className="pattern-kente pointer-events-none absolute inset-0 opacity-30" aria-hidden />
      <div className="relative">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-primary text-primary-foreground shadow-gold">
            <Sparkles className="h-4 w-4" />
          </span>
          <div>
            <div className="text-sm font-semibold">{title}</div>
            {description && (
              <div className="text-xs text-muted-foreground">{description}</div>
            )}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => ask(s)}
              className="rounded-full border bg-background/80 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-primary/5"
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
